import type { ZodType } from 'zod';
import { errorResponseSchema } from '@bekebi/contracts/common';

/**
 * The portal's HTTP client: same-origin `/api` requests with the session cookie. It moves JSON
 * and maps failures; it contains no business rules — the API is the source of truth and the
 * security boundary (authorisation is always enforced server-side).
 */

/** Any failed API call. `code` / `message` come from the API's uniform error body when present. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 401: no valid session. Kept distinct so callers can send the user to sign in. */
export class UnauthenticatedError extends ApiError {
  constructor(code: string, message: string) {
    super(401, code, message);
    this.name = 'UnauthenticatedError';
  }
}

export const isUnauthenticated = (error: unknown): error is UnauthenticatedError =>
  error instanceof UnauthenticatedError;

const GENERIC_MESSAGES: Record<string, string> = {
  network_error: 'The portal could not be reached. Check your connection and try again.',
  unexpected_response: 'The portal returned an unexpected response.',
  invalid_response: 'The portal returned data in an unexpected format.',
};

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Sent as JSON. */
  body?: unknown;
  /** When given, the successful response is validated against it (contracts from packages/contracts). */
  schema?: ZodType<T>;
  signal?: AbortSignal;
}

/**
 * Calls `/api${path}`. Resolves with the parsed body (validated when a schema is given), or
 * `undefined` for an empty 2xx body. Rejects with `UnauthenticatedError` on 401 and `ApiError`
 * otherwise — never with a raw Response or a JSON parse error.
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      headers,
      // The session cookie is same-origin; it is never sent anywhere else.
      credentials: 'same-origin',
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if ((error as { name?: string } | null)?.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', GENERIC_MESSAGES.network_error!);
  }

  const payload = await readJson(response);

  if (!response.ok) {
    const parsed = errorResponseSchema.safeParse(payload);
    const code = parsed.success ? parsed.data.error.code : 'unexpected_response';
    const message = parsed.success
      ? parsed.data.error.message
      : GENERIC_MESSAGES.unexpected_response!;
    if (response.status === 401) throw new UnauthenticatedError(code, message);
    throw new ApiError(response.status, code, message);
  }

  if (payload === undefined) return undefined as T;
  if (payload === INVALID_JSON) {
    throw new ApiError(
      response.status,
      'unexpected_response',
      GENERIC_MESSAGES.unexpected_response!,
    );
  }
  if (!options.schema) return payload as T;
  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(response.status, 'invalid_response', GENERIC_MESSAGES.invalid_response!);
  }
  return parsed.data;
}

const INVALID_JSON = Symbol('invalid-json');

/** The body as JSON; undefined when empty; INVALID_JSON when it is not JSON. */
async function readJson(response: Response): Promise<unknown> {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return INVALID_JSON;
  }
  if (text.trim() === '') return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return INVALID_JSON;
  }
}
